import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from '@web/components/ui/card';
import type { ContactWithDetails } from '@shared/types/ContactWithDetails';
import { trpc } from '@web/lib/trpc';
import { EditableText } from '@web/components/ui/editable-text';
import { Button } from '@web/components/ui/button';
import { toast } from 'sonner';

export default function ContactInfo({
  contact,
}: {
  contact: ContactWithDetails;
}) {
  const utils = trpc.useUtils();

  // Mutations to update contact information
  const { mutateAsync: updateContactBasicInfo } =
    trpc.contact.updateContactBasicInfo.useMutation({
      onSuccess: () => {
        void utils.contact.getContact.invalidate({ contactId: contact.id });
      },
    });

  const { mutateAsync: updateContactDetails } =
    trpc.contact.updateContactDetails.useMutation({
      onSuccess: () => {
        void utils.contact.getContact.invalidate({ contactId: contact.id });
      },
    });

  // Query to fetch medical information
  const { data: medicalInfo } =
    trpc.contact.getContactBasicMedicalInfo.useQuery({
      contactId: contact.id,
    });

  // Mutation to update medical information
  const { mutateAsync: updateContactBasicMedicalInfo } =
    trpc.contact.updateContactBasicMedicalInfo.useMutation({
      onSuccess: () => {
        void utils.contact.getContact.invalidate({ contactId: contact.id });
      },
    });

  const handleUpdateContactBasicInfo = async (
    details: Record<string, string>,
  ) => {
    try {
      await updateContactBasicInfo({
        contactId: contact.id,
        details,
      });

      toast.success('Contact information updated');
    } catch (error) {
      toast.error('Failed to update contact information');
      console.error(error);
    }
  };

  const handleUpdateContactDetails = async (
    details: Record<string, string>,
  ) => {
    try {
      await updateContactDetails({
        contactId: contact.id,
        details,
      });

      toast.success('Contact details updated');
    } catch (error) {
      toast.error('Failed to update contact details');
      console.error(error);
    }
  };

  const handleUpdateContactMedicalInfo = async (
    details: Record<string, string | number | boolean>,
  ) => {
    try {
      // Log the request for debugging
      console.log('Updating medical info with:', {
        contactId: contact.id,
        details,
      });

      await updateContactBasicMedicalInfo({
        contactId: contact.id,
        details,
      });

      toast.success('Medical information updated');
    } catch (error) {
      toast.error('Failed to update medical information');
      console.error('Medical info update error:', error);
    }
  };

  // Basic personal information
  const personalInfoFields = [
    {
      label: 'First Name',
      value: contact.firstName,
      field: 'firstName',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      editable: true,
      section: 'basic',
    },
    {
      label: 'Last Name',
      value: contact.lastName,
      field: 'lastName',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      editable: true,
      section: 'basic',
    },
    {
      label: 'Email',
      value: contact.email,
      field: 'email',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
      editable: true,
      section: 'basic',
    },
    {
      label: 'Phone',
      value: contact.phoneNumber,
      field: 'phoneNumber',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>
      ),
      editable: true,
      section: 'basic',
    },
    {
      label: 'Sex',
      value: contact.details.sex,
      field: 'sex',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      editable: true,
      section: 'details',
    },
    {
      label: 'Marital Status',
      value: contact.details.maritalStatus,
      field: 'maritalStatus',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ),
      editable: true,
      section: 'details',
    },
  ];

  // Contact tracking information
  const contactTrackingFields = [
    {
      label: 'Last Contact',
      value: contact.lastCommunicationDate
        ? new Date(contact.lastCommunicationDate).toLocaleDateString(
            undefined,
            {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            },
          )
        : 'No communication yet',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      editable: false,
    },
    {
      label: 'Contact ID',
      value: contact.id || 'Unknown',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
          />
        </svg>
      ),
      editable: false,
    },
  ];

  // Address information
  const addressFields = [
    {
      label: 'Address',
      value: contact.details.address,
      field: 'address',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      editable: true,
      section: 'details',
    },
    {
      label: 'City',
      value: contact.details.city,
      field: 'city',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
      editable: true,
      section: 'details',
    },
    {
      label: 'State',
      value: contact.details.state,
      field: 'state',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
          />
        </svg>
      ),
      editable: true,
      section: 'details',
    },
    {
      label: 'Zip Code',
      value: contact.details.zipCode,
      field: 'zipCode',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      ),
      editable: true,
      section: 'details',
    },
    {
      label: 'Country',
      value: contact.details.country,
      field: 'country',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      editable: true,
      section: 'details',
    },
  ];

  // Physical attributes
  const physicalAttributesFields = [
    {
      label: 'Height',
      value: medicalInfo?.height?.toString() || '',
      field: 'height',
      append: 'cm',
      type: 'number',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7l4-4m0 0l4 4m-4-4v18"
          />
        </svg>
      ),
    },
    {
      label: 'Weight',
      value: medicalInfo?.weight?.toString() || '',
      field: 'weight',
      append: 'kg',
      type: 'number',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
          />
        </svg>
      ),
    },
  ];

  // Health conditions
  const healthConditionsFields = [
    {
      label: 'Allergies',
      value: 'dust, pollen, peanuts', // TODO: implement allergies from allergies_contacts table
      field: 'allergies',
      type: 'text',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
    {
      label: 'Medications',
      value: medicalInfo?.medications ?? '',
      field: 'medications',
      type: 'text',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
          />
        </svg>
      ),
    },
    {
      label: 'Diseases',
      value: medicalInfo?.diseases ?? '',
      field: 'diseases',
      type: 'text',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  // Lifestyle habits
  const lifestyleFields = [
    {
      label: 'Smokes',
      value: medicalInfo?.smoke ? 'Yes' : 'No',
      field: 'smoke',
      type: 'boolean',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          />
        </svg>
      ),
    },
    {
      label: 'Drinks Alcohol',
      value: medicalInfo?.alcohol ? 'Yes' : 'No',
      field: 'alcohol',
      type: 'boolean',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Personal Information Card */}
      <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white p-0 shadow-sm transition-shadow hover:shadow-md">
        <CardHeader className="bg-primary/5 border-b sm:px-6 sm:py-4">
          <CardTitle className="flex items-center text-base font-semibold text-gray-800 sm:text-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="text-primary mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            Personal Information
            <span className="ml-2 text-xs font-normal italic text-gray-500">
              (double-click to edit)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-y-6">
            {personalInfoFields.map((item, index) => (
              <div key={index} className="flex flex-col">
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  {item.icon}
                  <span className="ml-2">{item.label}</span>
                </dt>
                {item.editable ? (
                  <EditableText
                    initialText={item.value ?? ''}
                    onSave={async (text) => {
                      const details: Record<string, string> = {};
                      details[item.field] = text;
                      if (item.section === 'basic') {
                        await handleUpdateContactBasicInfo(details);
                      } else {
                        await handleUpdateContactDetails(details);
                      }
                    }}
                  />
                ) : (
                  <dd className="mt-1 text-sm font-medium text-gray-900">
                    {item.value ?? 'Not provided'}
                  </dd>
                )}
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* Address Information Card */}
      <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white p-0 shadow-sm transition-shadow hover:shadow-md">
        <CardHeader className="bg-primary/5 border-b px-4 py-3 sm:px-6 sm:py-4">
          <CardTitle className="flex items-center text-base font-semibold text-gray-800 sm:text-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="text-primary mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Address Information
            <span className="ml-2 text-xs font-normal italic text-gray-500">
              (double-click to edit)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-y-6">
            {addressFields.map((item, index) => (
              <div key={index} className="flex flex-col">
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  {item.icon}
                  <span className="ml-2">{item.label}</span>
                </dt>
                {item.editable ? (
                  <EditableText
                    initialText={item.value ?? ''}
                    onSave={async (text) => {
                      const details: Record<string, string> = {};
                      details[item.field] = text;
                      await handleUpdateContactDetails(details);
                    }}
                  />
                ) : (
                  <dd className="mt-1 text-sm font-medium text-gray-900">
                    {item.value ?? 'Not provided'}
                  </dd>
                )}
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* System Information */}
      <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white p-0 shadow-sm transition-shadow hover:shadow-md">
        <CardHeader className="bg-primary/5 border-b sm:px-6 sm:py-4">
          <CardTitle className="flex items-center text-base font-semibold text-gray-800 sm:text-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="text-primary mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            System Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-y-6">
            {contactTrackingFields.map((item, index) => (
              <div key={index} className="flex flex-col">
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  {item.icon}
                  <span className="ml-2">{item.label}</span>
                </dt>
                <dd className="mt-1 text-sm font-medium text-gray-900">
                  {item.value || 'Not provided'}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* Medical Information Card */}
      <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white p-0 shadow-sm transition-shadow hover:shadow-md lg:col-span-2">
        <CardHeader className="bg-primary/5 border-b sm:px-6 sm:py-4">
          <CardTitle className="flex items-center text-base font-semibold text-gray-800 sm:text-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="text-primary mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            Medical Information
            <span className="ml-2 text-xs font-normal italic text-gray-500">
              (double-click to edit)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="space-y-6">
            {/* Physical Attributes Section */}
            <div>
              <h3 className="text-primary mb-3 border-b pb-1 text-sm font-medium">
                Physical Attributes
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-4 sm:gap-y-6">
                {medicalInfo &&
                  physicalAttributesFields.map((item, index) => (
                    <div key={index} className="flex flex-col">
                      <dt className="flex items-center text-sm font-medium text-gray-500">
                        {item.icon}
                        <span className="ml-2">{item.label}</span>
                      </dt>
                      <EditableText
                        initialText={item.value || ''}
                        append={item.append}
                        onSave={async (text) => {
                          try {
                            const details: Record<string, string | number> = {};

                            if (item.type === 'number' && text) {
                              details[item.field] = Number(text);
                            } else {
                              details[item.field] = text;
                            }

                            await handleUpdateContactMedicalInfo(details);
                          } catch (error) {
                            console.error(
                              `Error updating ${item.label}:`,
                              error,
                            );
                            toast.error(`Failed to update ${item.label}`);
                          }
                        }}
                      />
                    </div>
                  ))}
              </dl>
            </div>

            {/* Health Conditions Section */}
            <div>
              <h3 className="text-primary mb-3 border-b pb-1 text-sm font-medium">
                Health Conditions
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-3 sm:gap-y-6">
                {medicalInfo &&
                  healthConditionsFields.map((item, index) => (
                    <div key={index} className="flex flex-col">
                      <dt className="flex items-center text-sm font-medium text-gray-500">
                        {item.icon}
                        <span className="ml-2">{item.label}</span>
                      </dt>
                      <EditableText
                        initialText={item.value || ''}
                        onSave={async (text) => {
                          try {
                            const details: Record<string, string> = {};
                            details[item.field] = text;
                            await handleUpdateContactMedicalInfo(details);
                          } catch (error) {
                            console.error(
                              `Error updating ${item.label}:`,
                              error,
                            );
                            toast.error(`Failed to update ${item.label}`);
                          }
                        }}
                      />
                    </div>
                  ))}
              </dl>
            </div>

            {/* Lifestyle Habits Section */}
            <div>
              <h3 className="text-primary mb-3 border-b pb-1 text-sm font-medium">
                Lifestyle Habits
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-y-6">
                {medicalInfo &&
                  lifestyleFields.map((item, index) => (
                    <div key={index} className="flex flex-col">
                      <dt className="flex items-center text-sm font-medium text-gray-500">
                        {item.icon}
                        <span className="ml-2">{item.label}</span>
                      </dt>
                      <div className="mt-1 flex items-center gap-2 text-sm font-medium">
                        <span
                          className={
                            item.value === 'Yes'
                              ? 'font-bold text-red-500'
                              : 'font-bold text-green-500'
                          }
                        >
                          {item.value}
                        </span>
                        <Button
                          variant={
                            item.value === 'Yes' ? 'destructive' : 'outline'
                          }
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            const newValue = !(item.value === 'Yes');
                            const details: Record<string, boolean> = {};
                            details[item.field] = newValue;
                            handleUpdateContactMedicalInfo(details)
                              .then(() => {
                                // Optionally handle success
                              })
                              .catch((error: unknown) => {
                                console.error(
                                  'Error updating boolean field:',
                                  error,
                                );
                                toast.error(`Failed to update ${item.label}`);
                              });
                          }}
                        >
                          Toggle to {item.value === 'Yes' ? 'No' : 'Yes'}
                        </Button>
                      </div>
                    </div>
                  ))}
              </dl>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
